/**
 * Hook для работы с записями тренера
 */
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@shared/store/authStore';
import type { Trainer } from '@shared/types/trainer';

// Вспомогательная функция для расчета длительности
function calculateDuration(timeFrom: string, timeTo: string): number {
  const [fromHours, fromMinutes] = timeFrom.split(':').map(Number);
  const [toHours, toMinutes] = timeTo.split(':').map(Number);
  const fromTotalMinutes = fromHours * 60 + fromMinutes;
  const toTotalMinutes = toHours * 60 + toMinutes;
  return toTotalMinutes - fromTotalMinutes;
}

interface Booking {
  id: number | string;
  clientId: number;
  clientName: string;
  serviceName: string;
  serviceId: number;
  date: string;
  timeFrom: string;
  timeTo: string;
  duration: number;
  status: 'upcoming' | 'completed' | 'cancelled';
  price: number;
  type?: 'personal' | 'group' | 'court';
}

export function useTrainerBookings() {
  const { user, getApiClient } = useAuthStore();
  const trainer = user as Trainer;

  // Получение записей тренера на неделю
  const { data: todayBookings = [], isLoading } = useQuery({
    queryKey: ['trainer-bookings', user?.id, 'week'],
    queryFn: async () => {
      const apiClient = getApiClient();

      // Период запроса: сегодня + 14 дней (2 недели)
      // Используем локальную дату без конвертации в UTC
      const today = new Date();
      const twoWeeksLater = new Date(today);
      twoWeeksLater.setDate(today.getDate() + 14);

      // Форматируем дату в формате YYYY-MM-DD без конвертации в UTC
      const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const firstDate = formatLocalDate(today);
      const lastDate = formatLocalDate(twoWeeksLater);

      try {
        // Запрос записей через Universe API метод emp_booking_ex
        // Этот метод возвращает все типы записей: персональные, групповые, корты
        const response = await fetch('/api/universe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            method: 'emp_booking_ex',
            first_date: firstDate,
            last_date: lastDate,
            employees: [user.id]
          })
        });

        const data = await response.json();

        const allBookings: Booking[] = [];

        if (data.errorCode === '0') {
          // Обрабатываем персональные записи (emp_booking)
          if (data.emp_booking && data.emp_booking.length > 0) {
            const personalBookings = await Promise.all(data.emp_booking.map(async (booking: any) => {
              const bookingDate = booking.time_from ? booking.time_from.split(' ')[0] : firstDate;
              const timeFrom = booking.time_from ? booking.time_from.split(' ')[1] : '00:00';
              const timeTo = booking.time_to ? booking.time_to.split(' ')[1] : '00:00';
              const duration = calculateDuration(timeFrom, timeTo);

              const bookingDateTime = new Date(booking.time_from);
              const now = new Date();
              let status: 'upcoming' | 'completed' | 'cancelled' = 'upcoming';
              if (bookingDateTime < now) status = 'completed';

              // Загружаем данные клиента
              let clientName = '';
              try {
                const clientData = await apiClient.getClient(booking.ClientId);
                if (clientData) {
                  clientName = `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim();
                }
              } catch (error) {
                console.error(`Failed to load client ${booking.ClientId}:`, error);
              }

              return {
                id: booking.Id,
                clientId: booking.ClientId,
                clientName,
                serviceName: booking.Services?.[0]?.name || 'Персональная тренировка',
                serviceId: booking.Services?.[0]?.id || 0,
                date: bookingDate,
                timeFrom,
                timeTo,
                duration,
                status,
                price: 0,
                type: 'personal'
              };
            }));
            allBookings.push(...personalBookings);
          }

          // Обрабатываем групповые занятия (emp_lesson)
          if (data.emp_lesson && data.emp_lesson.length > 0) {
            const lessonBookings = data.emp_lesson.map((lesson: any) => {
              const bookingDate = lesson.date;
              const timeFrom = lesson.start_time;
              const timeTo = lesson.end_time;
              const duration = calculateDuration(timeFrom, timeTo);

              const bookingDateTime = new Date(`${bookingDate} ${timeFrom}`);
              const now = new Date();
              let status: 'upcoming' | 'completed' | 'cancelled' = 'upcoming';
              if (bookingDateTime < now) status = 'completed';

              // Количество клиентов в групповом занятии
              const clientsCount = lesson.clients?.length || 0;

              return {
                id: lesson.lesson_id,
                clientId: 0,
                clientName: `Группа (${clientsCount} чел)`,
                serviceName: lesson.service?.service_name || 'Групповое занятие',
                serviceId: lesson.service?.service_id || 0,
                date: bookingDate,
                timeFrom,
                timeTo,
                duration,
                status,
                price: 0,
                type: 'group'
              };
            });
            allBookings.push(...lessonBookings);
          }

          // Обрабатываем записи на корты (courts_booking)
          if (data.courts_booking && data.courts_booking.length > 0) {
            const courtBookings = await Promise.all(data.courts_booking.map(async (booking: any) => {
              const bookingDate = booking.time_from ? booking.time_from.split(' ')[0] : firstDate;
              const timeFrom = booking.time_from ? booking.time_from.split(' ')[1] : '00:00';
              const timeTo = booking.time_to ? booking.time_to.split(' ')[1] : '00:00';
              const duration = calculateDuration(timeFrom, timeTo);

              const bookingDateTime = new Date(booking.time_from);
              const now = new Date();
              let status: 'upcoming' | 'completed' | 'cancelled' = 'upcoming';
              if (bookingDateTime < now) status = 'completed';

              // Загружаем данные клиента
              let clientName = '';
              try {
                const clientData = await apiClient.getClient(booking.ClientId);
                if (clientData) {
                  clientName = `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim();
                }
              } catch (error) {
                console.error(`Failed to load client ${booking.ClientId}:`, error);
              }

              return {
                id: booking.Id,
                clientId: booking.ClientId,
                clientName,
                serviceName: `Корт #${booking.court_id}`,
                serviceId: booking.court_id || 0,
                date: bookingDate,
                timeFrom,
                timeTo,
                duration,
                status,
                price: 0,
                type: 'court'
              };
            }));
            allBookings.push(...courtBookings);
          }
        }

        return allBookings;
      } catch (error) {
        console.error('Error loading bookings:', error);
        return [];
      }
    },
    enabled: !!user?.id,
    refetchInterval: 60000 // Обновление каждую минуту
  });

  // Фильтруем записи на сегодня (используем локальную дату)
  const getTodayDateLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDateLocal();
  const todayBookingsFiltered = todayBookings.filter(b => b.date === todayDate);

  // Статистика за день (только сегодняшние записи)
  const stats = {
    todayBookings: todayBookingsFiltered.length,
    completedToday: todayBookingsFiltered.filter(b => b.status === 'completed').length,
    upcomingToday: todayBookingsFiltered.filter(b => b.status === 'upcoming').length,
  };

  // Ближайшие записи (следующие 3 из всех будущих)
  const now = new Date();
  const upcomingBookings = todayBookings
    .filter(b => {
      const bookingDateTime = new Date(`${b.date}T${b.timeFrom}`);
      return bookingDateTime >= now;
    })
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.timeFrom}`);
      const dateB = new Date(`${b.date}T${b.timeFrom}`);
      return dateA.getTime() - dateB.getTime();
    })
    .slice(0, 3);

  return {
    todayBookings,
    upcomingBookings,
    stats,
    isLoading
  };
}
